import useApi from 'hooks/useApi';
import api from 'libs/api';
import { invoiceEmailsRequired, requiredCheckbox, stringRequired } from 'libs/validations';
import React, { FunctionComponent, useMemo } from 'react';
import * as Yup from 'yup';
import { PropertyInfo, YourContact } from './Cards';
import PaymentMethodLayout from './Layout';
import useCommon from 'hooks/useCommon';
import useFetchResource from 'hooks/useFetchResource';
import useOnSubmit from 'hooks/useOnSubmit';
import { FormikErrors, FormikProps } from 'formik';
import { CreditCard } from './Cards/CreditCard';
import { IPlan } from 'domain/env/types';
import { IEmailInvoice } from 'hooks/useEmailInvoices';
import clonedeep from 'lodash.clonedeep';
import usePayments from 'pages/Administration/PlanAndBilling/usePayments';
import { useParams } from 'react-router-dom';
import useNavigator from 'hooks/useNavigator';
import useStripeCardSetup, { IStripeCardFormValues } from 'hooks/useStripeCardSetup';
import trackAddPaymentInfo from './trackAddPaymentInfo';

export interface ICreditCardFormValues extends IStripeCardFormValues {
  is_authorized_to_payments: boolean;
  company_name: string;
  user_email: string;
  legal_name: string;
  user_name: string;
  user_position: string;
  invoice_emails_attributes: IEmailInvoice[];
}

const validationSchema = Yup.object().shape({
  invoice_emails_attributes: invoiceEmailsRequired,
  is_authorized_to_payments: requiredCheckbox,
  legal_name: stringRequired,
  user_email: stringRequired,
  user_name: stringRequired,
  user_position: stringRequired,
});

const formCards = [
  {
    component: (props: FormikProps<ICreditCardFormValues>) => <PropertyInfo {...props} />,
    title: 'Property Information',
  },
  {
    component: (props: FormikProps<ICreditCardFormValues>) => <CreditCard {...props} />,
    title: 'Credit Card Information',
  },
  {
    component: () => <YourContact />,
    title: 'Your contact information',
  },
];

const CreditCardPage: FunctionComponent = () => {
  usePayments();
  const { guid } = useParams();
  const { navigateCallback } = useNavigator();
  const { getApiPath } = useApi();
  const { fetchCommon } = useCommon();
  const { resource: paymentTypes, isLoading } = useFetchResource<{
    invoice_emails_attributes: IEmailInvoice[];
    plan?: IPlan;
  }>({
    api: api.billing.payment.types.getOne,
    initialParams: { guid },
    initialValues: { invoice_emails_attributes: [] },
  });
  const { plan } = paymentTypes;
  const initialValues: ICreditCardFormValues = useMemo(() => {
    return {
      company_name: '',
      invoice_emails_attributes: clonedeep(paymentTypes.invoice_emails_attributes),
      is_authorized_to_payments: false,
      legal_name: '',
      user_email: '',
      user_name: '',
      user_position: '',
      credit_card_number: '',
      credit_card_expiry: '',
      credit_card_cvc: '',
    };
  }, [paymentTypes]);

  const onSubmit = useOnSubmit({
    api: getApiPath('billing.payment.credit', 'create'),
    deps: [guid],
    onSuccess: () => {
      trackAddPaymentInfo('credit_card', 'success');
      fetchCommon();
      navigateCallback('/dashboard')();
    },
    onError: () => trackAddPaymentInfo('credit_card', 'error'),
    params: { guid },
    serializer: (data: ICreditCardFormValues) => ({ resource: data }),
    validationErrorSerializer: (errors: FormikErrors<ICreditCardFormValues>) => ({
      ...errors,
      //@ts-ignore
      invoice_emails_attributes: errors['invoice_emails.email'],
    }),
    successMessage: 'Payment successfully saved',
  });

  const submitHandler = useStripeCardSetup<ICreditCardFormValues>({
    setupIntentApi: getApiPath('billing.payment.credit.setupIntent', 'create'),
    params: { guid },
    onSubmit,
    deps: [guid],
  });

  if (isLoading) return null;

  return (
    <PaymentMethodLayout<ICreditCardFormValues>
      plan={plan?.title}
      price={plan ? `${plan.price} / month` : ''}
      initialValues={initialValues}
      validationSchema={validationSchema}
      title="Credit Card"
      formCards={formCards}
      onSubmit={submitHandler}
    />
  );
};

export default CreditCardPage;
